import React, { useState } from 'react';
import { X, Shield, Download, XCircle, BellRing, AlertCircle, CheckCircle2, MessageSquare, Plus, Trash2, Sparkles, FileText, FileSpreadsheet, AlertTriangle, Clock, ExternalLink, ChevronRight, Info, ShieldCheck, Lock, Smartphone, CreditCard, Pointer } from 'lucide-react';
// Define types for our alerts
type AlertType = 'ai' | 'manual';
interface AIAlert {
  type: 'ai';
  description: string;
  id: string;
}
interface ManualAlert {
  type: 'manual';
  indicator: string;
  subCategory: string;
  option: string;
  id: string;
}
type Alert = AIAlert | ManualAlert;
// Define types for report options
type ReportType = 'summary' | 'detailed';
type FileFormat = 'pdf' | 'excel';
// Define type for risk events
interface RiskEvent {
  id: string;
  title: string;
  description: string;
  timestamp: string;
  timeAgo: string;
  severity: 'high' | 'medium' | 'low';
  category: string;
  details: {
    label: string;
    value: string;
    isHighlight?: boolean;
  }[];
}
// Define recommended action type
interface RecommendedAction {
  id: string;
  title: string;
  description: string;
  actionText: string;
  priority: 'high' | 'medium' | 'low';
  icon: React.ReactNode;
}
export const RiskPanel = ({
  onClose,
  toggleAIChat
}: {
  onClose: () => void;
  toggleAIChat: () => void;
}) => {
  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showEventsModal, setShowEventsModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<RiskEvent | null>(null);
  const [reportType, setReportType] = useState<ReportType>('summary');
  const [fileFormat, setFileFormat] = useState<FileFormat>('pdf');
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [activeTab, setActiveTab] = useState<'ai' | 'manual'>('ai');
  const [aiDescription, setAiDescription] = useState('');
  const [selectedIndicator, setSelectedIndicator] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [selectedOption, setSelectedOption] = useState('');
  const [alertMode, setAlertMode] = useState<'list' | 'create'>('list');
  // Recommended actions data
  const recommendedActions: RecommendedAction[] = [{
    id: '1',
    title: 'Block Credit Card',
    description: 'Transaction failed 3 times in last 2 hours',
    actionText: 'Block',
    priority: 'high',
    icon: <CreditCard size={18} className="text-purple-500" data-id="element-417" />
  }, {
    id: '2',
    title: 'Enable 2FA Authentication',
    description: 'Strengthen the account security with two-factor authentication',
    actionText: 'Enable Now',
    icon: <ShieldCheck size={18} className="text-blue-500" data-id="element-418" />
  }];
  // Mock risk events data
  const riskEvents: RiskEvent[] = [{
    id: '1',
    title: 'New device login detected',
    description: 'Login from iPhone Safari in Chicago, IL',
    timestamp: '2023-11-25T10:15:00',
    timeAgo: '2 hours ago',
    severity: 'high',
    category: 'Device Anomalies',
    details: [{
      label: 'Device',
      value: 'iPhone Safari',
      isHighlight: true
    }, {
      label: 'Location',
      value: 'Chicago, IL',
      isHighlight: true
    }, {
      label: 'IP Address',
      value: '192.168.1.254'
    }, {
      label: 'Time',
      value: '10:15 AM EST'
    }, {
      label: 'User',
      value: 'sarah.johnson@example.com'
    }, {
      label: 'Previous Device',
      value: 'Windows 11 / Chrome'
    }, {
      label: 'Risk Score Impact',
      value: '+15 points',
      isHighlight: true
    }]
  }, {
    id: '2',
    title: 'Unusual login time',
    description: 'Login at 3:24 AM outside normal hours',
    timestamp: '2023-11-25T03:24:00',
    timeAgo: '9 hours ago',
    severity: 'high',
    category: 'Access Patterns',
    details: [{
      label: 'Time',
      value: '3:24 AM EST',
      isHighlight: true
    }, {
      label: 'Usual Login Hours',
      value: '8 AM - 6 PM'
    }, {
      label: 'Device',
      value: 'Windows 11 / Chrome'
    }, {
      label: 'Location',
      value: 'New York, NY'
    }, {
      label: 'User',
      value: 'sarah.johnson@example.com'
    }, {
      label: 'Risk Score Impact',
      value: '+10 points',
      isHighlight: true
    }]
  }, {
    id: '3',
    title: 'Password reset attempt',
    description: 'Multiple password reset attempts',
    timestamp: '2023-11-24T22:12:00',
    timeAgo: '14 hours ago',
    severity: 'medium',
    category: 'Login Patterns',
    details: [{
      label: 'Attempts',
      value: '3 within 15 minutes',
      isHighlight: true
    }, {
      label: 'Success',
      value: 'Yes, on 3rd attempt'
    }, {
      label: 'Device',
      value: 'Windows 11 / Chrome'
    }, {
      label: 'Location',
      value: 'New York, NY'
    }, {
      label: 'IP Address',
      value: '192.168.1.123'
    }, {
      label: 'Risk Score Impact',
      value: '+5 points'
    }]
  }, {
    id: '4',
    title: 'Unusual transaction',
    description: 'Wire transfer to new recipient',
    timestamp: '2023-11-24T14:45:00',
    timeAgo: '22 hours ago',
    severity: 'medium',
    category: 'Transaction Risk',
    details: [{
      label: 'Amount',
      value: '$1,250.00'
    }, {
      label: 'Recipient',
      value: 'New (First transfer)',
      isHighlight: true
    }, {
      label: 'Account',
      value: 'Checking ****4567'
    }, {
      label: 'Time',
      value: '2:45 PM EST'
    }, {
      label: 'Device',
      value: 'Windows 11 / Chrome'
    }, {
      label: 'Risk Score Impact',
      value: '+5 points'
    }]
  }, {
    id: '5',
    title: 'Geographic anomaly',
    description: 'Login from unusual location',
    timestamp: '2023-11-24T09:30:00',
    timeAgo: '1 day ago',
    severity: 'high',
    category: 'Geographic Anomalies',
    details: [{
      label: 'Location',
      value: 'Chicago, IL',
      isHighlight: true
    }, {
      label: 'Usual Location',
      value: 'New York, NY'
    }, {
      label: 'Distance',
      value: '790 miles',
      isHighlight: true
    }, {
      label: 'Device',
      value: 'Android / Chrome'
    }, {
      label: 'Time',
      value: '9:30 AM EST'
    }, {
      label: 'Risk Score Impact',
      value: '+15 points',
      isHighlight: true
    }]
  }];
  const handleCardClick = (title: string) => {
    setSelectedCard(title);
  };
  const closeCardDetails = () => {
    setSelectedCard(null);
  };
  const toggleAlertModal = () => {
    setShowAlertModal(!showAlertModal);
    if (!showAlertModal) {
      setAlertMode('list');
    }
  };
  const toggleDownloadModal = () => {
    setShowDownloadModal(!showDownloadModal);
    // Reset to defaults when opening
    if (!showDownloadModal) {
      setReportType('summary');
      setFileFormat('pdf');
    }
  };
  const toggleEventsModal = () => {
    setShowEventsModal(!showEventsModal);
    if (!showEventsModal) {
      setSelectedEvent(null);
    }
  };
  const openEventDetails = (event: RiskEvent) => {
    setSelectedEvent(event);
  };
  const closeEventDetails = () => {
    setSelectedEvent(null);
  };
  const openCreateAlert = () => {
    setAlertMode('create');
    resetAlertForm();
  };
  const resetAlertForm = () => {
    setAiDescription('');
    setSelectedIndicator('');
    setSelectedSubCategory('');
    setSelectedOption('');
    setActiveTab('ai');
  };
  const saveAlert = () => {
    const newAlert: Alert = activeTab === 'ai' ? {
      type: 'ai',
      description: aiDescription,
      id: Date.now().toString()
    } : {
      type: 'manual',
      indicator: selectedIndicator,
      subCategory: selectedSubCategory,
      option: selectedOption,
      id: Date.now().toString()
    };
    setAlerts([...alerts, newAlert]);
    setAlertMode('list');
    resetAlertForm();
  };
  const deleteAlert = (id: string) => {
    setAlerts(alerts.filter(alert => alert.id !== id));
  };
  const downloadReport = () => {
    // In a real application, this would trigger the actual download
    // For now, we'll just close the modal and show what would be downloaded
    console.log(`Downloading ${reportType} report as ${fileFormat}`);
    setShowDownloadModal(false);
  };
  // Sample data for manual alert options
  const alertOptions = {
    indicators: ['Risk Score', 'Device Anomalies', 'Geographic Anomalies', 'Access Patterns', 'Login Patterns'],
    subCategories: {
      'Risk Score': ['Above Threshold', 'Rapid Change', 'Sustained High Level'],
      'Device Anomalies': ['New Device', 'Unusual Browser', 'VPN Detection'],
      'Geographic Anomalies': ['Unusual Location', 'Impossible Travel', 'High Risk Region'],
      'Access Patterns': ['Unusual Time', 'Unusual Features', 'Unusual Navigation'],
      'Login Patterns': ['Failed Attempts', 'Password Changes', '2FA Status Changes']
    },
    options: {
      'Above Threshold': ['> 50', '> 75', '> 90'],
      'Rapid Change': ['> 10 points in 24h', '> 20 points in 24h', '> 30 points in 24h'],
      'Sustained High Level': ['> 70 for 3 days', '> 80 for 3 days', '> 90 for 3 days'],
      'New Device': ['Any new device', 'Mobile device', 'Desktop device'],
      'Unusual Browser': ['Any unusual browser', 'Outdated browser', 'Rare browser'],
      'VPN Detection': ['Any VPN', 'Foreign VPN', 'Known suspicious VPN'],
      'Unusual Location': ['Any new location', 'International location', 'High risk country'],
      'Impossible Travel': ['Any impossible travel', '> 500 miles in 1h', '> 1000 miles in 3h'],
      'High Risk Region': ['Any high risk region', 'Specific countries', 'Known fraud regions'],
      'Unusual Time': ['Outside business hours', 'Late night (12am-5am)', 'Weekend'],
      'Unusual Features': ['Wire transfers', 'Account settings', 'Security settings'],
      'Unusual Navigation': ['Direct to sensitive areas', 'Skipping normal flow', 'Rapid page switching'],
      'Failed Attempts': ['> 3 in 1h', '> 5 in 24h', '> 10 in 7 days'],
      'Password Changes': ['Any change', '> 2 in 7 days', 'After suspicious activity'],
      '2FA Status Changes': ['Disabled', 'Changed', 'Failed verification']
    }
  };
  const handleAnalyzeWithAI = () => {
    onClose(); // Close the Risk Panel first
    setTimeout(() => {
      toggleAIChat(true); // Then open the AI Chat with auto-message flag
    }, 100); // Small delay to ensure smooth transition
  };
  return <div className="w-[400px] bg-white border-l border-r flex flex-col relative" data-id="element-419">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-3 py-6 flex items-center justify-between" data-id="element-420">
        <div className="flex items-center" data-id="element-421">
          <Shield size={20} data-id="element-422" />
          <span className="font-medium pl-2 text-md" data-id="element-423">Risk Assessment</span>
        </div>
        <div className="flex items-center gap-3" data-id="element-424">
          <div className="relative cursor-pointer" onClick={toggleAlertModal} data-id="element-425">
            <BellRing size={20} data-id="element-426" />
            {alerts.length > 0 && <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center" data-id="element-427">
                <span className="text-[10px] text-white font-medium" data-id="element-428">
                  {alerts.length}
                </span>
              </div>}
          </div>
          <Download size={20} className="cursor-pointer" title="Export Report" onClick={toggleDownloadModal} data-id="element-429" />
          <button onClick={onClose} data-id="element-430">
            <X size={20} data-id="element-431" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className={`flex-1 p-4 overflow-y-auto ${selectedCard || showAlertModal || showDownloadModal || showEventsModal || selectedEvent ? 'blur-sm' : ''}`} data-id="element-432">
        {/* Risk Score */}
        <div className="bg-red-50 rounded-lg p-4 mb-4" data-id="element-433">
          <div className="flex flex-col items-center" data-id="element-434">
            <div className="relative w-20 h-20 mb-3" data-id="element-435">
              <svg viewBox="0 0 36 36" className="w-20 h-20 transform -rotate-90" data-id="element-436">
                <path d="M18 2.0845
                    a 15.9155 15.9155 0 0 1 0 31.831
                    a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#f87171" strokeWidth="3" data-id="element-437" />
                <path d="M18 2.0845
                    a 15.9155 15.9155 0 0 1 0 31.831
                    a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#e6e6e6" strokeWidth="3" strokeDasharray="25, 100" data-id="element-438" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center" data-id="element-439">
                <span className="text-2xl font-bold text-red-500" data-id="element-440">75</span>
              </div>
            </div>
            <div className="flex flex-col items-center gap-2 text-center" data-id="element-441">
              <div className="text-md text-red-600 font-bold uppercase tracking-wide" data-id="element-442">
                HIGH RISK
              </div>
              <div className="text-lg font-medium" data-id="element-443">Current Risk Score</div>
              <div className="text-xs text-gray-600" data-id="element-444">
                Last updated 3 hrs ago
              </div>
            </div>
          </div>
        </div>
        {/* Compact Active Alerts line */}
        <div className="flex items-center justify-between text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-4 cursor-pointer hover:bg-amber-100 transition-colors" onClick={toggleEventsModal} data-id="element-445">
          <div className="flex items-center gap-2" data-id="element-446">
            <AlertTriangle size={16} className="text-amber-600" data-id="element-447" />
            <span data-id="element-448">3 new alerts in past 24 hours</span>
          </div>
          <ChevronRight size={16} className="text-amber-600" data-id="element-449" />
        </div>
        {/* Comparison Cards */}
        <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-450">
          {/* vs. Similar Profiles Card */}
          <div className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm" data-id="element-451">
            <div className="text-sm text-gray-800 mb-2 font-medium" data-id="element-452">
              vs. Similar Profiles
            </div>
            <div className="text-green-500 text-lg font-medium mb-2" data-id="element-453">
              67% Lower
            </div>
            <div className="w-full h-2 bg-gray-200 rounded-full" data-id="element-454">
              <div className="w-1/3 h-full bg-green-400 rounded-full" data-id="element-455"></div>
            </div>
          </div>

          {/* Risk Forensics Trend Card */}
          <div className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm" data-id="element-456">
            <div className="text-sm text-gray-800 font-medium mb-2" data-id="element-457">
              Risk Forensics Trend
            </div>
            <div className="text-red-500 text-lg font-medium mb-2" data-id="element-458">
              Volatile
            </div>
            <div className="flex gap-1" data-id="element-459">
              {[1, 2, 3, 4, 5, 6, 7].map(i => <div key={i} className={`flex-1 h-2 rounded-sm ${i < 2 ? 'bg-gray-300' : i === 3 ? 'bg-green-400' : i === 4 ? 'bg-gray-300' : i === 5 ? 'bg-green-400' : 'bg-red-400'}`} data-id="element-460"></div>)}
            </div>
          </div>
        </div>
        {/* Risk Indicators Title */}
        <div className="flex items-center justify-between mb-3" data-id="element-461">
          <h3 className="text-sm font-medium text-gray-700" data-id="element-462">Risk Indicators</h3>
          <div className="text-xs text-gray-500 flex items-center" data-id="element-463">
            <Pointer size={12} className="mr-1" data-id="element-464" />
            Click cards for details
          </div>
        </div>
        {/* Risk Cards */}
        <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-465">
          <RiskCard title="Device Anomalies" level="High" color="red" onClick={() => handleCardClick('Device Anomalies')} data-id="element-466" />
          <RiskCard title="Geographic Anomalies" level="High" color="red" onClick={() => handleCardClick('Geographic Anomalies')} data-id="element-467" />
          <RiskCard title="Access Patterns" level="High" color="red" onClick={() => handleCardClick('Access Patterns')} data-id="element-468" />
          <RiskCard title="High Risk Login Indicator" level="Medium" color="yellow" onClick={() => handleCardClick('High Risk Login Indicator')} data-id="element-469" />
          <RiskCard title="Unusual Transaction Risk" level="Low" color="green" onClick={() => handleCardClick('Unusual Transaction Risk')} data-id="element-470" />
          <RiskCard title="Login Frequency Pattern" level="Insufficient Data" color="gray" onClick={() => handleCardClick('Login Frequency Pattern')} data-id="element-471" />
        </div>
        {/* Recommended Actions Section */}
        <div className="mb-4" data-id="element-472">
          <h3 className="text-sm font-medium text-gray-700 mb-3" data-id="element-473">
            Recommended Actions
          </h3>
          <div className="space-y-3" data-id="element-474">
            {recommendedActions.map(action => <div key={action.id} className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm flex items-start gap-3" data-id="element-475">
                <div className="mt-0.5" data-id="element-476">{action.icon}</div>
                <div className="flex-1" data-id="element-477">
                  <div className="flex justify-between" data-id="element-478">
                    <div className="font-medium text-sm" data-id="element-479">{action.title}</div>
                    {action.priority === 'high' && <div className="bg-red-100 text-red-800 text-xs px-1.5 py-0.5 rounded uppercase font-medium" data-id="element-480">
                        High
                      </div>}
                  </div>
                  <div className="text-xs text-gray-600 mb-2" data-id="element-481">
                    {action.description}
                  </div>
                  <button className="text-xs bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded" data-id="element-482">
                    {action.actionText}
                  </button>
                </div>
              </div>)}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className={`p-4 border-t ${selectedCard || showAlertModal || showDownloadModal || showEventsModal || selectedEvent ? 'blur-sm' : ''}`} data-id="element-483">
        <button onClick={handleAnalyzeWithAI} className="flex justify-center gap-2 items-center w-full bg-purple-500 hover:bg-purple-600 text-white rounded-md py-2 font-medium" data-id="element-484">
          <span data-id="element-485">
            <Sparkles size={18} className="text-white" data-id="element-486" />
          </span>{' '}
          Analyze with AI
        </button>
      </div>

      {/* Card Details Popup */}
      {selectedCard && <div className="absolute inset-0 flex items-center justify-center p-4 z-10" data-id="element-487">
          <CardDetailsPopup title={selectedCard} onClose={closeCardDetails} data-id="element-488" />
        </div>}

      {/* Alert Modal */}
      {showAlertModal && <div className="absolute inset-0 flex items-center justify-center p-4 z-10" data-id="element-489">
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden w-[350px]" data-id="element-490">
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-3 flex items-center justify-between" data-id="element-491">
              <h3 className="font-medium" data-id="element-492">
                {alertMode === 'list' ? 'Alerts' : 'Create Alert'}
              </h3>
              <button onClick={toggleAlertModal} className="text-white" data-id="element-493">
                <X size={18} data-id="element-494" />
              </button>
            </div>
            {alertMode === 'list' ? <div className="p-4" data-id="element-495">
                {alerts.length === 0 ? <div className="text-center py-6" data-id="element-496">
                    <div className="text-gray-500 mb-4" data-id="element-497">
                      No alerts configured
                    </div>
                    <button onClick={openCreateAlert} className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-md font-medium flex items-center justify-center gap-1 mx-auto" data-id="element-498">
                      <Plus size={16} data-id="element-499" />
                      Create Alert
                    </button>
                  </div> : <>
                    <div className="mb-4 max-h-[50vh] overflow-y-auto" data-id="element-500">
                      {alerts.map(alert => <div key={alert.id} className="border rounded-md p-3 mb-3 last:mb-0" data-id="element-501">
                          <div className="flex justify-between items-start mb-2" data-id="element-502">
                            <div className="flex items-center gap-1.5" data-id="element-503">
                              {alert.type === 'ai' ? <MessageSquare size={16} className="text-blue-500" data-id="element-504" /> : <AlertCircle size={16} className="text-purple-500" data-id="element-505" />}
                              <span className="font-medium" data-id="element-506">
                                {alert.type === 'ai' ? 'AI Alert' : 'Manual Alert'}
                              </span>
                            </div>
                            <button onClick={() => deleteAlert(alert.id)} className="text-gray-400 hover:text-red-500" data-id="element-507">
                              <Trash2 size={16} data-id="element-508" />
                            </button>
                          </div>
                          {alert.type === 'ai' ? <div className="text-sm text-gray-600 break-words" data-id="element-509">
                              {alert.description}
                            </div> : <div className="text-sm text-gray-600" data-id="element-510">
                              <div data-id="element-511">
                                <span className="font-medium" data-id="element-512">Indicator:</span>{' '}
                                {alert.indicator}
                              </div>
                              <div data-id="element-513">
                                <span className="font-medium" data-id="element-514">Condition:</span>{' '}
                                {alert.subCategory}
                              </div>
                              <div data-id="element-515">
                                <span className="font-medium" data-id="element-516">Threshold:</span>{' '}
                                {alert.option}
                              </div>
                            </div>}
                        </div>)}
                    </div>
                    <button onClick={openCreateAlert} className="w-full bg-blue-500 hover:bg-blue-600 text-white py-2 rounded-md font-medium flex items-center justify-center gap-1" data-id="element-517">
                      <Plus size={16} data-id="element-518" />
                      Add New Alert
                    </button>
                  </>}
              </div> : <>
                {/* Tabs */}
                <div className="flex border-b" data-id="element-519">
                  <div className={`flex-1 py-3 text-center cursor-pointer ${activeTab === 'ai' ? 'border-b-2 border-blue-500 text-blue-600 font-medium' : 'text-gray-500'}`} onClick={() => setActiveTab('ai')} data-id="element-520">
                    <div className="flex items-center justify-center gap-2" data-id="element-521">
                      <Sparkles size={16} data-id="element-522" />
                      <span data-id="element-523">AI Alert</span>
                    </div>
                  </div>
                  <div className={`flex-1 py-3 text-center cursor-pointer ${activeTab === 'manual' ? 'border-b-2 border-blue-500 text-blue-600 font-medium' : 'text-gray-500'}`} onClick={() => setActiveTab('manual')} data-id="element-524">
                    <div className="flex items-center justify-center gap-2" data-id="element-525">
                      <AlertCircle size={16} data-id="element-526" />
                      <span data-id="element-527">Conditional Alert</span>
                    </div>
                  </div>
                </div>

                {/* Tab Content */}
                <div className="p-4" data-id="element-528">
                  {activeTab === 'ai' ? <div data-id="element-529">
                      <div className="mb-4" data-id="element-530">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-531">
                          Describe your alert in natural language
                        </label>
                        <textarea className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" rows={4} placeholder="e.g., Alert me when the risk score goes above 80 or if there's unusual login activity from a new device" value={aiDescription} onChange={e => setAiDescription(e.target.value)} data-id="element-532"></textarea>
                      </div>
                    </div> : <div data-id="element-533">
                      <div className="mb-3" data-id="element-534">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-535">
                          Risk Indicator
                        </label>
                        <select className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" value={selectedIndicator} onChange={e => {
                  setSelectedIndicator(e.target.value);
                  setSelectedSubCategory('');
                  setSelectedOption('');
                }} data-id="element-536">
                          <option value="" data-id="element-537">Select an indicator</option>
                          {alertOptions.indicators.map(indicator => <option key={indicator} value={indicator} data-id="element-538">
                              {indicator}
                            </option>)}
                        </select>
                      </div>
                      {selectedIndicator && <div className="mb-3" data-id="element-539">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-540">
                            Sub-Category
                          </label>
                          <select className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" value={selectedSubCategory} onChange={e => {
                  setSelectedSubCategory(e.target.value);
                  setSelectedOption('');
                }} data-id="element-541">
                            <option value="" data-id="element-542">Select a sub-category</option>
                            {alertOptions.subCategories[selectedIndicator as keyof typeof alertOptions.subCategories]?.map(subCategory => <option key={subCategory} value={subCategory} data-id="element-543">
                                {subCategory}
                              </option>)}
                          </select>
                        </div>}
                      {selectedSubCategory && <div className="mb-3" data-id="element-544">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-545">
                            Alert Option
                          </label>
                          <select className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" value={selectedOption} onChange={e => setSelectedOption(e.target.value)} data-id="element-546">
                            <option value="" data-id="element-547">Select an option</option>
                            {alertOptions.options[selectedSubCategory as keyof typeof alertOptions.options]?.map(option => <option key={option} value={option} data-id="element-548">
                                {option}
                              </option>)}
                          </select>
                        </div>}
                    </div>}

                  {/* Buttons */}
                  <div className="flex gap-3 mt-6" data-id="element-549">
                    <button onClick={() => setAlertMode('list')} className="flex-1 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 py-2 rounded-md font-medium" data-id="element-550">
                      Cancel
                    </button>
                    <button onClick={saveAlert} className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-2 rounded-md font-medium flex items-center justify-center gap-1" disabled={activeTab === 'ai' && !aiDescription || activeTab === 'manual' && (!selectedIndicator || !selectedSubCategory || !selectedOption)} data-id="element-551">
                      <Plus size={16} data-id="element-552" />
                      Set Alert
                    </button>
                  </div>
                </div>
              </>}
          </div>
        </div>}

      {/* Download Modal */}
      {showDownloadModal && <div className="absolute inset-0 flex items-center justify-center p-4 z-10" data-id="element-553">
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden w-[350px]" data-id="element-554">
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-3 flex items-center justify-between" data-id="element-555">
              <h3 className="font-medium" data-id="element-556">Export Report</h3>
              <button onClick={toggleDownloadModal} className="text-white" data-id="element-557">
                <X size={18} data-id="element-558" />
              </button>
            </div>
            <div className="p-4" data-id="element-559">
              <div className="mb-4" data-id="element-560">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-561">
                  Report Type
                </label>
                <div className="grid grid-cols-2 gap-3" data-id="element-562">
                  <div className={`border rounded-md p-3 flex flex-col items-center gap-2 cursor-pointer transition-colors ${reportType === 'summary' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`} onClick={() => setReportType('summary')} data-id="element-563">
                    <FileText size={24} className={reportType === 'summary' ? 'text-blue-500' : 'text-gray-400'} data-id="element-564" />
                    <span className={`text-sm font-medium ${reportType === 'summary' ? 'text-blue-700' : 'text-gray-700'}`} data-id="element-565">
                      Summary
                    </span>
                  </div>
                  <div className={`border rounded-md p-3 flex flex-col items-center gap-2 cursor-pointer transition-colors ${reportType === 'detailed' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`} onClick={() => {
                setReportType('detailed');
                // // If switching to detailed, automatically set to Excel
                // setFileFormat('excel')
              }} data-id="element-566">
                    <FileSpreadsheet size={24} className={reportType === 'detailed' ? 'text-blue-500' : 'text-gray-400'} data-id="element-567" />
                    <span className={`text-sm font-medium ${reportType === 'detailed' ? 'text-blue-700' : 'text-gray-700'}`} data-id="element-568">
                      Detailed
                    </span>
                  </div>
                </div>
              </div>
              <div className="mb-6" data-id="element-569">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-570">
                  File Format
                </label>
                <div className="grid grid-cols-2 gap-3" data-id="element-571">
                  <div className={`border rounded-md p-3 flex items-center gap-2 cursor-pointer transition-colors ${fileFormat === 'pdf' ? 'border-blue-500 bg-blue-50' : reportType === 'detailed' ? 'border-gray-200 bg-gray-100 opacity-50 cursor-not-allowed' : 'border-gray-200 hover:bg-gray-50'}`} onClick={() => {
                if (reportType !== 'detailed') {
                  setFileFormat('pdf');
                }
              }} data-id="element-572">
                    <div className="w-6 h-6 flex items-center justify-center" data-id="element-573">
                      <div className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center text-white text-[10px] font-bold" data-id="element-574">
                        PDF
                      </div>
                    </div>
                    <span className={`text-sm font-medium ${fileFormat === 'pdf' ? 'text-blue-700' : 'text-gray-700'}`} data-id="element-575">
                      PDF
                    </span>
                  </div>
                  <div className={`border rounded-md p-3 flex items-center gap-2 cursor-pointer transition-colors ${fileFormat === 'excel' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`} onClick={() => setFileFormat('excel')} data-id="element-576">
                    <div className="w-6 h-6 flex items-center justify-center" data-id="element-577">
                      <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center text-white text-[10px] font-bold" data-id="element-578">
                        XL
                      </div>
                    </div>
                    <span className={`text-sm font-medium ${fileFormat === 'excel' ? 'text-blue-700' : 'text-gray-700'}`} data-id="element-579">
                      Excel
                    </span>
                  </div>
                </div>
              </div>
              <div className="text-xs text-gray-500 mb-4" data-id="element-580">
                {reportType === 'summary' ? 'Summary reports include high-level risk indicators and scores.' : 'Detailed reports include all risk factors, historical data, and complete analytics.'}
              </div>
              <button onClick={downloadReport} className="w-full bg-blue-500 hover:bg-blue-600 text-white py-2 rounded-md font-medium flex items-center justify-center gap-1" data-id="element-581">
                <Download size={16} data-id="element-582" />
                Download Report
              </button>
            </div>
          </div>
        </div>}

      {/* Events Modal */}
      {showEventsModal && !selectedEvent && <div className="absolute inset-0 flex items-center justify-center p-4 z-10" data-id="element-583">
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden w-[350px] max-h-[80vh] flex flex-col" data-id="element-584">
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-3 flex items-center justify-between" data-id="element-585">
              <h3 className="font-medium" data-id="element-586">Active Alerts</h3>
              <button onClick={toggleEventsModal} className="text-white" data-id="element-587">
                <X size={18} data-id="element-588" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1" data-id="element-589">
              {riskEvents.map(event => <div key={event.id} className="p-3 border-b hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => openEventDetails(event)} data-id="element-590">
                  <div className="flex items-start gap-3" data-id="element-591">
                    <div className={`mt-0.5 p-1 rounded-full ${event.severity === 'high' ? 'bg-red-100' : event.severity === 'medium' ? 'bg-amber-100' : 'bg-green-100'}`} data-id="element-592">
                      <AlertTriangle size={16} className={`
                          ${event.severity === 'high' ? 'text-red-500' : event.severity === 'medium' ? 'text-amber-500' : 'text-green-500'}
                        `} data-id="element-593" />
                    </div>
                    <div className="flex-1" data-id="element-594">
                      <div className="flex justify-between items-start" data-id="element-595">
                        <div className="font-medium text-gray-900" data-id="element-596">
                          {event.title}
                        </div>
                        <div className="flex items-center text-xs text-gray-500 whitespace-nowrap ml-2" data-id="element-597">
                          <Clock size={12} className="mr-1" data-id="element-598" />
                          {event.timeAgo}
                        </div>
                      </div>
                      <div className="text-sm text-gray-600 mt-1" data-id="element-599">
                        {event.description}
                      </div>
                      <div className="flex items-center mt-2" data-id="element-600">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${event.severity === 'high' ? 'bg-red-100 text-red-800' : event.severity === 'medium' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`} data-id="element-601">
                          {event.severity.charAt(0).toUpperCase() + event.severity.slice(1)}{' '}
                          Risk
                        </span>
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full ml-2" data-id="element-602">
                          {event.category}
                        </span>
                        <ChevronRight size={16} className="text-gray-400 ml-auto" data-id="element-603" />
                      </div>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
        </div>}
      {/* Event Details Modal */}
      {selectedEvent && <div className="absolute inset-0 flex items-center justify-center p-4 z-10" data-id="element-604">
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden w-[350px] max-h-[80vh] flex flex-col" data-id="element-605">
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-3 flex items-center justify-between" data-id="element-606">
              <div className="flex items-center" data-id="element-607">
                <button onClick={closeEventDetails} className="text-white mr-2 hover:bg-white/10 rounded p-1" data-id="element-608">
                  <ChevronRight size={18} className="transform rotate-180" data-id="element-609" />
                </button>
                <h3 className="font-medium" data-id="element-610">Event Details</h3>
              </div>
              <button onClick={toggleEventsModal} className="text-white" data-id="element-611">
                <X size={18} data-id="element-612" />
              </button>
            </div>
            <div className="p-4 border-b" data-id="element-613">
              <div className="flex items-center gap-2 mb-2" data-id="element-614">
                <div className={`p-1 rounded-full ${selectedEvent.severity === 'high' ? 'bg-red-100' : selectedEvent.severity === 'medium' ? 'bg-amber-100' : 'bg-green-100'}`} data-id="element-615">
                  <AlertTriangle size={16} className={`
                      ${selectedEvent.severity === 'high' ? 'text-red-500' : selectedEvent.severity === 'medium' ? 'text-amber-500' : 'text-green-500'}
                    `} data-id="element-616" />
                </div>
                <div className="font-medium text-gray-900" data-id="element-617">
                  {selectedEvent.title}
                </div>
              </div>
              <div className="text-sm text-gray-600 mb-3" data-id="element-618">
                {selectedEvent.description}
              </div>
              <div className="flex flex-wrap gap-2 mb-2" data-id="element-619">
                <span className={`text-xs px-2 py-0.5 rounded-full ${selectedEvent.severity === 'high' ? 'bg-red-100 text-red-800' : selectedEvent.severity === 'medium' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`} data-id="element-620">
                  {selectedEvent.severity.charAt(0).toUpperCase() + selectedEvent.severity.slice(1)}{' '}
                  Risk
                </span>
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full" data-id="element-621">
                  {selectedEvent.category}
                </span>
                <div className="flex items-center text-xs text-gray-500 ml-auto" data-id="element-622">
                  <Clock size={12} className="mr-1" data-id="element-623" />
                  {selectedEvent.timeAgo}
                </div>
              </div>
            </div>
            <div className="overflow-y-auto flex-1 p-2" data-id="element-624">
              <div className="divide-y divide-gray-200" data-id="element-625">
                {selectedEvent.details.map((detail, index) => <div key={index} className="py-3 px-2 flex justify-between" data-id="element-626">
                    <div className="text-gray-600 text-sm" data-id="element-627">{detail.label}</div>
                    <div className={`text-sm font-medium ${detail.isHighlight ? 'text-red-600' : 'text-gray-800'}`} data-id="element-628">
                      {detail.value}
                    </div>
                  </div>)}
              </div>
            </div>
            <div className="p-3 border-t flex justify-center" data-id="element-629">
              <button className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center" onClick={() => {
            // In a real app, this would navigate to a detailed analysis page
            console.log('View detailed analysis for event:', selectedEvent.id);
          }} data-id="element-630">
                <ExternalLink size={14} className="mr-1" data-id="element-631" />
                View Detailed Analysis
              </button>
            </div>
          </div>
        </div>}
    </div>;
};
const RiskCard = ({
  title,
  level,
  color,
  onClick
}: {
  title: string;
  level: string;
  color: string;
  onClick: () => void;
}) => {
  const getColorClass = (color: string) => {
    switch (color) {
      case 'red':
        return 'bg-red-500';
      case 'yellow':
        return 'bg-yellow-500';
      case 'green':
        return 'bg-green-500';
      default:
        return 'bg-white';
    }
  };
  return <div className="bg-gray-600 text-white p-3 rounded-lg flex h-24 cursor-pointer hover:bg-gray-700 transition-colors" onClick={onClick} data-id="element-632">
      <div className="flex flex-col justify-between flex-1 gap-3" data-id="element-633">
        <div className="text-sm font-medium" data-id="element-634">{title}</div>
        <div className="flex items-center" data-id="element-635">
          <div className={`${getColorClass(color)} w-1.5 h-6 mr-3 rounded-full`} data-id="element-636"></div>
          <div className="text-sm" data-id="element-637">{level}</div>
        </div>
      </div>
    </div>;
};
const CardDetailsPopup = ({
  title,
  onClose
}: {
  title: string;
  onClose: () => void;
}) => {
  // Mock data for the popup
  const mockData = {
    'Device Anomalies': [{
      label: 'Risk Level',
      value: 'High',
      isHighlight: true
    }, {
      label: 'New Device Detected',
      value: 'Yes - iPhone Safari',
      isHighlight: true
    }, {
      label: 'Device ID',
      value: 'MAC-78:3A:9E:1F:C2:D4'
    }, {
      label: 'Device Location',
      value: 'Chicago, IL'
    }, {
      label: 'Last Known Device',
      value: 'Windows 11 / Chrome'
    }, {
      label: 'Browser Fingerprint',
      value: 'Changed',
      isHighlight: true
    }, {
      label: 'Browser',
      value: 'Chrome 120.0.6099'
    }, {
      label: 'Operating System',
      value: 'iOS'
    }, {
      label: 'VPN/Proxy Detected',
      value: 'Yes',
      isHighlight: true
    }, {
      label: 'Last Updated',
      value: '3 hours ago'
    }],
    'Geographic Anomalies': [{
      label: 'Risk Level',
      value: 'High',
      isHighlight: true
    }, {
      label: 'Current Location',
      value: 'Chicago, IL'
    }, {
      label: 'Usual Location',
      value: 'New York, NY'
    }, {
      label: 'Distance from Normal',
      value: '790 miles',
      isHighlight: true
    }, {
      label: 'Travel Time Analysis',
      value: 'Possible (last login: 8 hours ago)'
    }, {
      label: 'High Risk Region',
      value: 'No'
    }, {
      label: 'Last Updated',
      value: '25 minutes ago'
    }],
    'Access Patterns': [{
      label: 'Risk Level',
      value: 'High',
      isHighlight: true
    }, {
      label: 'Login Time',
      value: '3:24 AM (unusual)'
    }, {
      label: 'Session Duration',
      value: '47 mins (normal)'
    }, {
      label: 'Features Accessed',
      value: 'Wire transfers, Account settings'
    }, {
      label: 'Navigation Pattern',
      value: 'Unusual (direct to wire transfers)'
    }, {
      label: 'Last Updated',
      value: '1 day ago'
    }],
    'High Risk Login Indicator': [{
      label: 'Risk Level',
      value: 'Medium',
      isHighlight: true
    }, {
      label: 'Failed Attempts',
      value: '2 in last hour'
    }, {
      label: 'Password Changes',
      value: '1 in last week'
    }, {
      label: '2FA Status',
      value: 'Disabled'
    }, {
      label: 'Login Velocity',
      value: 'Normal'
    }, {
      label: 'Credential Source',
      value: 'No Breach Detected'
    }, {
      label: 'Last Updated',
      value: '3 hours ago'
    }],
    'Unusual Transaction Risk': [{
      label: 'Risk Level',
      value: 'Low',
      isHighlight: true
    }, {
      label: 'Last Transaction Amount',
      value: '$120.00'
    }, {
      label: 'Typical Range',
      value: '$50 - $500'
    }, {
      label: 'Transaction Time',
      value: 'Business Hours'
    }, {
      label: 'Beneficiary',
      value: 'Known Business Partner'
    }, {
      label: 'Frequency',
      value: 'Monthly (Regular Pattern)'
    }, {
      label: 'Last Updated',
      value: '1 min ago'
    }],
    'Login Frequency Pattern': [{
      label: 'Risk Level',
      value: 'Insufficient Data',
      isHighlight: true
    }, {
      label: 'Account Age',
      value: '4 days'
    }, {
      label: 'Login Count',
      value: '3 total'
    }, {
      label: 'Baseline Required',
      value: '10+ logins'
    }, {
      label: 'Current Frequency',
      value: '0.75 logins/day'
    }, {
      label: 'Last Updated',
      value: '6 days ago'
    }]
  };
  const data = mockData[title as keyof typeof mockData] || [];
  return <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden max-w-[350px] w-full" data-id="element-638">
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-3 flex items-center justify-between" data-id="element-639">
        <h3 className="font-medium" data-id="element-640">{title}</h3>
        <button onClick={onClose} className="text-white" data-id="element-641">
          <X size={18} data-id="element-642" />
        </button>
      </div>
      <div className="p-2 max-h-[70vh] overflow-y-auto" data-id="element-643">
        <div className="divide-y divide-gray-200" data-id="element-644">
          {data.map((item, index) => <div key={index} className="py-3 px-2 flex justify-between" data-id="element-645">
              <div className="text-gray-600 text-sm" data-id="element-646">{item.label}</div>
              <div className={`text-sm font-medium ${item.isHighlight ? 'text-red-600' : 'text-gray-800'}`} data-id="element-647">
                {item.value}
              </div>
            </div>)}
        </div>
      </div>
    </div>;
};